import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import { toast } from 'react-toastify';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatCollectionName = (name) => {
  const specialCases = {
    tallyitems: 'Tally Items',
    companyheads: 'Company Heads',
    tallysyncs: 'Tally Syncs',
    tallyparameters: 'Tally Parameters',
    doorsparameters: 'Door Parameters',
    loginhistories: 'Login History',
    superadmins: 'Super Admins',
    activitylogs: 'Activity Logs',
    tilesparameters: 'Tiles Parameters',
    productinwards: 'Product Inwards',
    productoutwards: 'Product Outwards',
    tilesinwards: 'Tiles Inwards',
    tilesoutwards: 'Tiles Outwards',
    tileslocationtransfers: 'Tiles Location Transfers',
    sequencecounters: 'Sequence Counters',
    deliveryorderwithdiscounts: 'Delivery Orders with Discounts',
    deliveryorders: 'Delivery Orders',
    fieldvisits: 'Field Visits',
    interiors: 'Interiors',
    reminders: 'Reminders',
    telecallingpayments: 'Tele Calling Payments',
    telecallingsales: 'Tele Calling Sales',

  };

  return specialCases[name] || name.charAt(0).toUpperCase() + name.slice(1);
};

const groupCollections = (collections) => {
  const groups = {
    users: ['superadmins', 'admins', 'companyheads', 'employees'],
    business: ['companies', 'locations', 'roles'],
    inventory: ['tallyitems', 'items', 'doors', 'tiles'],
    parameters: ['tallyparameters', 'doorsparameters', 'tilesparameters'],
    sales: ['quotations', 'deliveryorders', 'deliveryorderwithdiscounts', 'estimates'],
    CRM: ['customers', 'dealers', 'buyers', 'suppliers', 'transporters', 'interiors'],
    activities: ['fieldvisits', 'telecallingsales', 'telecallingpayments', 'reminders', 'activitylogs', 'inwards', 'outwards', 'tilesinwards', 'tilesoutwards', 'tileslocationtransfers','loginhistories','tallysyncs'],
    sync: ['tallysyncs', 'tallyunits', 'tallygodowns', 'tallystockgroups'],
  };

  const groupedData = {};
  Object.entries(groups).forEach(([groupName, collectionNames]) => {
    groupedData[groupName] = collectionNames.reduce((acc, name) => {
      if (collections[name] !== undefined) {
        acc[name] = collections[name];
      }
      return acc;
    }, {});
  });

  return groupedData;
};

const DashboardHome = () => {
  const [stats, setStats] = useState({
    collections: {},
    logins: { dates: [], counts: [] }
  });

  const chartRef = useRef(null);

  const fetchDashboardData = async () => {
    try {
      const [collectionsResponse, loginStatsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/super-admin/collection-counts`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/super-admin/login-stats`)
      ]);

      setStats({
        collections: collectionsResponse.data,
        logins: loginStatsResponse.data
      });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const loginChartData = {
    labels: stats.logins.dates,
    datasets: [
      {
        label: 'Logins',
        data: stats.logins.counts,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        display: true,
        text: 'Login Activity (Last 7 Days)'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      {Object.entries(groupCollections(stats.collections)).map(([group, collections]) => (
        <div key={group} className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4 text-gray-700 capitalize">{group}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(collections).map(([collection, count]) => (
              <div 
                key={collection} 
                className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <h3 className="text-sm font-medium text-gray-600">{formatCollectionName(collection)}</h3>
                <p className="text-2xl font-bold text-indigo-600 mt-2">
                  {new Intl.NumberFormat().format(count)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* Login Statistics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4 text-gray-700">Login Activity</h2>
        <div style={{ height: '400px' }}>
          <Line
            ref={chartRef}
            data={loginChartData}
            options={chartOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;